import { getTablePermissions, hasPermission } from "@/lib/permissions";
import type { UserRoleType } from "@/types/auth";
import { router } from "expo-router";

export const getExpenseTableConfig = (userRoles: UserRoleType[] | null, displayNames) => {
  const permissions = getTablePermissions(userRoles, "expenses");

  // Build readonly columns - shift is conditional on permissions
  const readonlyColumns: (string | [string, (key: string, value: any, row: any) => any])[] = [
    "id",
    "created_at",
    ["client", (_key, _value, row) => row.client && router.setParams({ panel: "client", id: row.client.id })],
    ["project", (_key, _value, row) => row.project && router.setParams({ panel: "project", id: row.project.id })],
  ];

  // Only add clickable shift column if user has shifts.read
  if (hasPermission(userRoles, "shifts", "read")) {
    readonlyColumns.push([
      "shift",
      (_key, _value, row) => row.shift && router.setParams({ panel: "shift", id: row.shift.id }),
    ]);
  }

  // Add clickable billing_items column if user has invoices.read (billing items)
  if (hasPermission(userRoles, "invoices", "read")) {
    readonlyColumns.push([
      "billing_items",
      (_key, _value, row) =>
        row.billing_items?.[0] && router.setParams({ panel: "billing_item", id: row.billing_items[0] }),
    ]);
  }

  return {
    entityName: "Expense",
    entityNamePlural: "Expenses",

    tableConfig: {
      headerFunc: (name: string) => {
        switch (name) {
          case "id":
            return `${displayNames?.expenses?.name ?? "Expense"} Id`;
          case "client":
            return displayNames?.clients?.name ?? "Client";
          case "project":
            return displayNames?.projects?.name ?? "Project";
          case "shift":
            return displayNames?.shifts?.name ?? "Shift";
          case "billing_items":
            return "Billing Item";
          default:
            return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
        }
      },
      ignore: [
        "tenant_id",
        "meta_data",
        "comments",
        "history",
        "project_data",
        "project_id",
        "client_id",
        "shift_id",
        "shift_name",
        "user_id",
      ],
      readonly: readonlyColumns,
      validators: {},
      cellTextCallback: (value: any, key: string) => {
        if (value == null) return "";
        if (key === "id") return value.toString().slice(0, 6).toUpperCase();
        if (key === "amount") return `$${Number(value).toFixed(2)}`;
        if (key === "shift" && typeof value === "object") {
          const name = value.name || "";
          const id = value.id ? value.id.toString().slice(0, 6).toUpperCase() : "";
          return name && id ? `${name} - ${id}` : name || id || "";
        }
        if (key === "billing_items") {
          if (!Array.isArray(value) || value.length === 0) return "—";
          return value.map((id: string) => id.slice(0, 6).toUpperCase()).join(", ");
        }
        return typeof value === "string" ? value : JSON.stringify(value);
      },
      dataTypes: {
        created_at: "date",
        amount: "number",
        client: {
          type: "linked_record" as const,
          labelKey: "name",
          valueKey: "id",
          truncate: 6,
        },
        project: {
          type: "linked_record" as const,
          labelKey: "name",
          valueKey: "id",
          truncate: 6,
        },
        shift: {
          type: "linked_record" as const,
          labelKey: "name",
          valueKey: "id",
          truncate: 6,
        },
        user: {
          type: "linked_record" as const,
          labelKey: "name",
          valueKey: "id",
          truncate: 6,
        },
        status: {
          type: "enum",
          options: [
            { value: "PENDING", color: "pending" },
            { value: "APPROVED", color: "complete" },
            { value: "REJECTED", color: "attention" },
          ],
        },
      },
    },
    filterChoices: [
      {
        name: "id",
        kind: "text" as const,
        displayName: `${displayNames?.expenses?.name ?? "Expense"} Id`,
        menus: ["filter", "visible"] as const,
      },
      { name: "description", kind: "text", menus: ["filter", "visible"] as const },
      {
        name: "client",
        displayName: `${displayNames?.clients?.name ?? "Client"}`,
        kind: "linked_record" as const,
        menus: ["filter", "visible", "groupby", "orderby"] as const,
        linkedRecord: { labelKey: "name", valueKey: "id", truncate: 6 },
      },
      {
        name: "project",
        displayName: displayNames?.projects?.name ?? "Project",
        kind: "linked_record" as const,
        menus: ["filter", "visible", "groupby", "orderby"] as const,
        linkedRecord: { labelKey: "name", valueKey: "id", truncate: 6 },
      },
      {
        name: "shift",
        displayName: displayNames?.shifts?.name ?? "Shift",
        kind: "linked_record" as const,
        menus: ["filter", "visible", "groupby", "orderby"] as const,
        linkedRecord: { labelKey: "name", valueKey: "id", truncate: 6 },
      },
      { name: "amount", kind: "number", menus: ["filter", "visible"] as const },
      { name: "status", kind: "text", menus: ["filter", "visible"] as const },
      // { name: "billing_items", kind: "text", displayName: "Billing Item", menus: ["filter", "visible"] as const },
      { name: "created_at", kind: "date", menus: ["filter", "visible", "orderby"] as const },
    ],
    ...permissions,
    storageKey: "expense_flyout",
  };
};
